/**
 * Confirms bulk asset status changes and backstops any missing smart-crop
 * rejection labels before the update can be submitted.
 */

import React from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { type AssetDto, type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import { AssetList } from './AssetList';
import { ASSET_STATUSES } from 'src/components/assets/asset-status';
import { formatScheduleGapRanges, type ScheduleWarning } from 'src/utils/computeScheduleWarnings';
import { assetStrategyKey } from 'src/utils/assetKeys';
import type { RejectReasonSelection } from '../RejectReasonSelect';
import {
  getIncompleteRejectAssets,
  RejectReasonBackstop,
} from './RejectReasonBackstop';

export interface AssetApprovalModalModalProps {
  action: string;
  assetBatches: AssetDto[][];
  rejectedAssetsOnProcess: AssetDto[][];
  campaigns?: CampaignDesiredStateDto[];
  scheduleWarnings?: Map<string, ScheduleWarning[]>;
  // productStrategyId → display name, shown per row in the confirm preview.
  strategyLabels?: Map<string, string>;
  incompleteRejectAssets?: readonly AssetDto[];
  isLoading?: boolean;
  open: boolean;
  rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  onRejectReasonChange?: (asset: AssetDto, value: RejectReasonSelection) => void;
}

export const AssetApprovalModal = (props: AssetApprovalModalModalProps) => {
  const {
    action,
    assetBatches,
    rejectedAssetsOnProcess,
    campaigns,
    scheduleWarnings,
    strategyLabels,
    incompleteRejectAssets = [],
    isLoading = false,
    open,
    rejectReasonValues,
    onCancel,
    onConfirm,
    onRejectReasonChange,
  } = props;
  const { t } = useTranslation();
  const hasIncompleteRejectReasons = getIncompleteRejectAssets(
    incompleteRejectAssets,
    rejectReasonValues,
  ).length > 0;

  // Check if any assets being approved belong to ADSP campaigns
  const hasAdspAssets = React.useMemo(() => {
    if (!campaigns || assetBatches.length === 0) {
      return false;
    }
    const allAssets = assetBatches.flat();

    return allAssets.some(asset =>
      asset.campaignIds.some(campaignId => {
        const campaign = campaigns.find(c => c.campaignId === campaignId);
        return campaign?.publisher === 'adsp';
      })
    );
  }, [assetBatches, campaigns]);

  // Show ADSP guidance alert only for ADSP assets and Approved action
  const shouldShowAdspAlert = hasAdspAssets && action === ASSET_STATUSES.APPROVED;

  // Summarize non-blocking schedule warnings across the assets being approved.
  // Overlap / past-event-end are counted per asset; coverage gaps are counted per distinct title
  // (GTI), since a gap is a group-level property shared by all of a title's assets. Gap warnings
  // that carry details also produce one "title X is uncovered during ..." line per distinct
  // title + gap set.
  const scheduleWarningSummary = React.useMemo(() => {
    const empty = {
      overlap: 0,
      pastEventEnd: 0,
      gapGtis: 0,
      gapDetails: [] as Array<{ key: string; title: string; ranges: string }>,
    };
    if (!scheduleWarnings || action !== ASSET_STATUSES.APPROVED) {
      return empty;
    }
    let overlap = 0;
    let pastEventEnd = 0;
    const gapGtis = new Set<string>();
    const gapDetails = new Map<string, { key: string; title: string; ranges: string }>();
    for (const asset of assetBatches.flat()) {
      const warnings = scheduleWarnings.get(assetStrategyKey(asset));
      if (!warnings?.length) continue;
      for (const warning of warnings) {
        if (warning.type === 'OVERLAP') overlap++;
        else if (warning.type === 'PAST_EVENT_END') pastEventEnd++;
        else if (warning.type === 'COVERAGE_GAP') {
          const gti = asset.titles[0]?.gti;
          if (gti) gapGtis.add(gti);
          if (warning.titleName && warning.gaps?.length) {
            const ranges = formatScheduleGapRanges(warning.gaps);
            const key = `${warning.titleName}|${ranges}`;
            if (!gapDetails.has(key)) gapDetails.set(key, { key, title: warning.titleName, ranges });
          }
        }
      }
    }
    return {
      overlap,
      pastEventEnd,
      gapGtis: gapGtis.size,
      gapDetails: Array.from(gapDetails.values()),
    };
  }, [scheduleWarnings, action, assetBatches]);

  const hasScheduleWarnings =
    scheduleWarningSummary.overlap > 0 ||
    scheduleWarningSummary.pastEventEnd > 0 ||
    scheduleWarningSummary.gapGtis > 0;

  return (
    <Modal
      data-testid="asset-action-confirmation-modal"
      open={open}
      scrollContainer="viewport"
      title={t('header_asset_status_update_modal')}
      width="max(800px, 90vw)"
    >

      <Column alignmentHorizontal="start" width="100%">
        {shouldShowAdspAlert && (
          <Alert size="medium" type='theme'> {t('adsp_approval_guidance_message')} </Alert>
        )}

        {hasScheduleWarnings && (
          <Alert data-testid="asset-schedule-warning-summary" size="medium" type="warning">
            <Column spacing="100">
              {scheduleWarningSummary.overlap > 0 && (
                <Text>{t('asset_schedule_warning_overlap_summary', { count: scheduleWarningSummary.overlap })}</Text>
              )}
              {scheduleWarningSummary.pastEventEnd > 0 && (
                <Text>{t('asset_schedule_warning_past_event_end_summary', { count: scheduleWarningSummary.pastEventEnd })}</Text>
              )}
              {scheduleWarningSummary.gapGtis > 0 && (
                <Text>{t('asset_schedule_warning_coverage_gap_summary', { count: scheduleWarningSummary.gapGtis })}</Text>
              )}
              {scheduleWarningSummary.gapDetails.map((detail) => (
                <Text data-testid="asset-schedule-warning-gap-detail" key={detail.key}>
                  {t('asset_schedule_warning_coverage_gap_detail', { title: detail.title, ranges: detail.ranges })}
                </Text>
              ))}
            </Column>
          </Alert>
        )}

        <RejectReasonBackstop
          assets={incompleteRejectAssets}
          rejectReasonValues={rejectReasonValues}
          onRejectReasonChange={onRejectReasonChange}
        />

        {assetBatches.length !== 0 && (
          <AssetList approvalStatus={action} batches={assetBatches} strategyLabels={strategyLabels} />
        )}
        {rejectedAssetsOnProcess.length !== 0 && (
          <AssetList approvalStatus={ASSET_STATUSES.REJECTED} batches={rejectedAssetsOnProcess} strategyLabels={strategyLabels}/>
        )}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {isLoading && (
            <Row
              data-testid="assset-action-loader"
              spacingInset="none 300"
              wrap="none"
            >
              <Loader size="medium" />
              <Text>{t('loader_loading')}</Text>
            </Row>
          )}

          <Button
            data-testid="cancel-asset-action"
            disabled={isLoading}
            size="small"
            type="secondary"
            onClick={onCancel}
          >
            {t('buttons_cancel')}
          </Button>
          <Button
            data-testid="confirm-asset-action"
            disabled={isLoading || hasIncompleteRejectReasons}
            size="small"
            type="primary"
            onClick={() => onConfirm()}
          >
            {t('buttons_confirm')}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
